import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';
import Layout from '../components/Layout';
import client from '../api/client';

const CLIENT_KEY = "live_ck_vZnjEJeQVxelE9dpbzb9rPmOoBN0";

const PLAN_INFO: Record<string, Record<string, { amount: number; label: string }>> = {
  basic: {
    monthly: { amount: 13900, label: "Basic 월간" },
    yearly:  { amount: 141600, label: "Basic 연간 (월 11,800원)" },
  },
  pro: {
    monthly: { amount: 29900, label: "Pro 월간" },
    yearly:  { amount: 286800, label: "Pro 연간 (월 23,900원)" },
  },
};

const BILLING_LABEL: Record<string, string> = {
  monthly: "월간", yearly: "연간"
};

const PLAN_LABEL: Record<string, string> = {
  basic: "Basic", pro: "Pro"
};

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan") || "basic";
  const [billingType, setBillingType] = useState<"monthly" | "yearly">("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const info = PLAN_INFO[plan]?.[billingType];
  const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
  const isUpgrade = preview?.has_subscription && preview?.current_plan !== plan;

  useEffect(() => {
    setPreviewLoading(true);
    client.get(`/payment/billing/upgrade-preview?new_plan=${plan}&billing_type=${billingType}`)
      .then(r => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [plan, billingType]);

  const handleCheckout = async () => {
    if (!info) return;
    setLoading(true);
    setError("");
    try {
      if (isUpgrade) {
        // 업그레이드 - 기존 빌링키로 차액 결제
        const r = await client.post("/payment/billing/upgrade", {
          new_plan: plan,
          billing_type: billingType
        });
        const stored = localStorage.getItem("user") ? "local" : "session";
        const u = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
        u.plan = plan;
        if (stored === "local") localStorage.setItem("user", JSON.stringify(u));
        else sessionStorage.setItem("user", JSON.stringify(u));
        navigate("/checkout/success?plan=" + plan + "&billingType=" + billingType + "&upgraded=true");
      } else {
        const { loadTossPayments } = await import("@tosspayments/tosspayments-sdk");
        const tossPayments = await loadTossPayments(CLIENT_KEY);
        const billing = tossPayments.payment({ customerKey: `user_${user.id}` });
        await billing.requestBillingAuth({
          method: "CARD",
          successUrl: `${window.location.origin}/checkout/success?plan=${plan}&billingType=${billingType}`,
          failUrl: `${window.location.origin}/checkout/fail`,
          customerEmail: user.email,
          customerName: user.name || "고객",
        });
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || "결제 오류가 발생했습니다");
      setLoading(false);
    }
  };

  const chargeAmount = isUpgrade ? (preview?.charge_amount ?? info?.amount) : info?.amount;

  const s: any = {
    card: { background: "#161B27", border: "0.5px solid #2A3045", borderRadius: 12, padding: 28, marginBottom: 16 },
    infoBox: { background: "#0F2A22", border: "0.5px solid #00D4AA44", borderRadius: 8, padding: "14px 16px", marginBottom: 16 },
    upgradeBox: { background: "#1A1500", border: "0.5px solid #F6AD5544", borderRadius: 8, padding: "14px 16px", marginBottom: 16 },
    btn: (active: boolean) => ({
      flex: 1, padding: "12px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
      background: active ? "#00D4AA" : "#1A2035",
      border: active ? "none" : "0.5px solid #2A3045",
      color: active ? "#0A1F18" : "#8892A4",
    }),
    submitBtn: { width: "100%", background: "#00D4AA", border: "none", borderRadius: 8, padding: "14px", color: "#0A1F18", fontSize: 16, fontWeight: 700, cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1 },
  };

  return (
    <Layout>
      <div style={{ maxWidth: 480 }}>
        <h2 style={{ color: "#E8ECF4", fontSize: 20, fontWeight: 700, marginBottom: 24 }}>구독 신청</h2>

        {/* 현재 구독 중 업그레이드 안내 */}
        {!previewLoading && isUpgrade && (
          <div style={s.upgradeBox}>
            <div style={{ color: "#F6AD55", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>⚡ 플랜 업그레이드</div>
            <div style={{ color: "#E8ECF4", fontSize: 13, lineHeight: 1.8 }}>
              현재 <strong style={{ color: "#00D4AA" }}>{PLAN_LABEL[preview.current_plan]} {BILLING_LABEL[preview.current_billing_type]} 플랜</strong> 이용 중입니다.<br />
              잔여 {preview.remaining_days}일 크레딧 <strong style={{ color: "#F6AD55" }}>{preview.remaining_credit.toLocaleString()}원</strong>이 차감되며,<br />
              <strong style={{ color: "#E8ECF4" }}>{(preview.charge_amount).toLocaleString()}원</strong>만 추가 결제됩니다.
            </div>
          </div>
        )}

        <div style={s.card}>
          <div style={{ color: "#8892A4", fontSize: 12, marginBottom: 12 }}>플랜 선택</div>
          <div style={{ color: "#00D4AA", fontSize: 22, fontWeight: 800, marginBottom: 4, textTransform: "uppercase" as const }}>{plan}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button style={s.btn(billingType === "monthly")} onClick={() => setBillingType("monthly")}>
              월간 구독<br /><span style={{ fontSize: 12 }}>{plan === "basic" ? "13,900원/월" : "29,900원/월"}</span>
            </button>
            <button style={s.btn(billingType === "yearly")} onClick={() => setBillingType("yearly")}>
              연간 구독 <span style={{ fontSize: 11, color: billingType === "yearly" ? "#0A1F18" : "#F6AD55" }}>{plan === "basic" ? "15% 할인" : "20% 할인"}</span><br />
              <span style={{ fontSize: 12 }}>{plan === "basic" ? "11,800원/월" : "23,900원/월"}</span>
            </button>
          </div>
        </div>

        <div style={s.card}>
          <div style={{ color: "#8892A4", fontSize: 12, marginBottom: 16 }}>구독 요약</div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ color: "#E8ECF4", fontSize: 14 }}>{info?.label}</span>
            <span style={{ color: "#E8ECF4", fontSize: 14, fontWeight: 700 }}>{info?.amount.toLocaleString()}원</span>
          </div>
          {isUpgrade && preview?.remaining_credit > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: "#8892A4", fontSize: 13 }}>잔여 크레딧 차감</span>
              <span style={{ color: "#F56565", fontSize: 13 }}>- {preview.remaining_credit.toLocaleString()}원</span>
            </div>
          )}
          {isUpgrade && (
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "0.5px solid #2A3045", marginTop: 4 }}>
              <span style={{ color: "#E8ECF4", fontSize: 14, fontWeight: 700 }}>지금 결제할 금액</span>
              <span style={{ color: "#00D4AA", fontSize: 16, fontWeight: 800 }}>{(preview?.charge_amount ?? 0).toLocaleString()}원</span>
            </div>
          )}
          {billingType === "yearly" && !isUpgrade && (
            <div style={{ color: "#00D4AA", fontSize: 12, textAlign: "right" as const }}>
              월 {Math.round((info?.amount || 0) / 12).toLocaleString()}원 × 12개월
            </div>
          )}
          <div style={{ borderTop: "0.5px solid #2A3045", marginTop: 16, paddingTop: 16 }}>
            <div style={{ color: "#4A5568", fontSize: 12, lineHeight: 1.8 }}>
              • 잔여 크레딧 차감 후 차액만 결제됩니다<br />
              • 월간 구독: 매월 자동 갱신 / 연간 구독: 1년 후 자동 갱신<br />
              • 언제든지 마이페이지에서 구독 취소 가능
            </div>
          {!isUpgrade && (
            <div style={{ background: "#0F2A22", border: "1px solid #00D4AA55", borderRadius: 8, padding: "12px 14px", marginTop: 12 }}>
              <div style={{ color: "#00D4AA", fontSize: 13, fontWeight: 700, marginBottom: 4 }}>🎁 첫 달 무료 혜택</div>
              <div style={{ color: "#8892A4", fontSize: 12, lineHeight: 1.7 }}>
                오늘은 결제되지 않습니다.<br />
                카드 등록 후 <strong style={{ color: "#E8ECF4" }}>30일간 무료</strong>로 이용하신 후,<br />
                무료 기간 종료 시 자동으로 첫 결제가 진행됩니다.
              </div>
            </div>
          )}
          </div>
        </div>

        {isUpgrade && (
          <div style={{ background: "#0F1117", border: "0.5px solid #2A3045", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ color: "#8892A4", fontSize: 12, lineHeight: 1.8 }}>
              💡 이번 달만 <strong style={{ color: "#00D4AA" }}>{chargeAmount?.toLocaleString()}원</strong>이 청구되며,
              다음 결제일부터 <strong style={{ color: "#E8ECF4" }}>{info?.amount.toLocaleString()}원</strong>이 정상 청구됩니다.
            </div>
          </div>
        )}
        {error && <div style={{ color: "#F56565", fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <button style={s.submitBtn} onClick={handleCheckout} disabled={loading}>
          {loading ? "처리 중..." : isUpgrade
            ? `${(preview?.charge_amount ?? 0).toLocaleString()}원 업그레이드`
            : '첫 달 무료로 시작하기'}
        </button>
        <button onClick={() => navigate("/pricing")} style={{ width: "100%", background: "none", border: "none", color: "#4A5568", fontSize: 13, marginTop: 12, cursor: "pointer" }}>
          취소
        </button>

      
      <div style={{ background: "#161B27", border: "0.5px solid #2A3045", borderRadius: 12, padding: 24, marginTop: 24 }}>
        <div style={{ color: "#E8ECF4", fontSize: 14, fontWeight: 700, marginBottom: 16 }}>자주 묻는 질문</div>
        {[
          { q: "구독을 취소하면 바로 이용이 중단되나요?", a: "아닙니다. 취소 후에도 현재 구독 기간 만료일까지 정상 이용 가능합니다. 만료일 이후 자동 갱신이 중단되고 Free 플랜으로 전환됩니다." },
          { q: "자동 갱신은 언제 결제되나요?", a: "구독 만료일에 자동으로 갱신됩니다. 갱신 3일 전에 안내 이메일이 발송되며, 원하지 않으시면 그 전에 마이페이지에서 취소해 주세요." },
          { q: "연간 구독 환불이 가능한가요?", a: "결제 후 7일 이내 전액 환불 가능합니다 (전자상거래법). 7일 초과 시 환불이 불가하며, 구독 취소 후 만료일까지 정상 이용 가능합니다." },
          { q: "플랜을 중간에 변경할 수 있나요?", a: "언제든지 상위 플랜으로 업그레이드 가능합니다. 잔여 기간을 일할 계산하여 차액만 결제하시면 즉시 적용됩니다." },
          { q: "결제 실패 시 어떻게 되나요?", a: "자동 갱신 결제 실패 시 다음 날 자동으로 재시도합니다. 3회 연속 실패 시 Free 플랜으로 전환되며, 매 실패마다 안내 이메일이 발송됩니다." },
          { q: "동시 접속 제한이란?", a: "하나의 계정으로 동시에 접속 가능한 기기 수를 제한합니다. 초과 시 가장 오래된 세션이 자동 로그아웃됩니다." },
        ].map(item => (
          <div key={item.q} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "0.5px solid #1E2535" }}>
            <div style={{ color: "#E8ECF4", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Q. {item.q}</div>
            <div style={{ color: "#8892A4", fontSize: 12, lineHeight: 1.6 }}>A. {item.a}</div>
          </div>
        ))}
      </div>
      </div>
    </Layout>
  );
}
